/**
 * TEMP-H-023: Mosaic Loader Component
 * 
 * 3D depth-cycling grid loader with deterministic scramble order.
 * All animations use motion tokens - no hardcoded timings.
 */

import React, { useMemo, useRef, useEffect } from 'react';
import '@/styles/mosaic.css';

interface MosaicLoaderProps {
  /** Grid dimensions - will auto-calculate if not provided */
  rows?: number;
  cols?: number;
  /** Density-based sizing - overrides rows/cols */
  density?: 'sparse' | 'normal' | 'dense';
  /** Overall animation duration in ms */
  durationMs?: number;
  /** Seed for deterministic tile order */
  seed?: string | number;
  /** Enable continuous depth cycling (default: true) */
  loopDepth?: boolean;
  /** Manual progress control (0-1) - overrides internal timeline */
  progress?: number;
  /** Visual variant */
  variant?: 'default' | 'accent' | 'success' | 'gradient' | 'agi' | 'agi-accent' | 'agi-cyberpunk' | 'dark' | 'dark-soft';
  /** Size variant */
  size?: 'small' | 'normal' | 'large';
  /** Spacing variant */
  spacing?: 'dense' | 'normal' | 'spacious';
  /** Additional CSS classes */
  className?: string;
}

// Simple seeded PRNG (Mulberry32)
function mulberry32(seed: number) {
  let state = seed;
  return function() {
    state |= 0; state = state + 0x6D2B79F5 | 0;
    let t = Math.imul(state ^ state >>> 15, state | 1);
    t = t ^ t + Math.imul(t ^ t >>> 7, t | 61) | 0;
    return ((t ^ t >>> 14) >>> 0) / 4294967296;
  };
}

// Hash string to number for seeding
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32bit integer
  }
  return hash >>> 0; // Ensure unsigned 32-bit for deterministic seeding
}

// Hook for generating deterministic mosaic sequence
function useMosaicSequence(seed: string | number, rows: number, cols: number) {
  return useMemo(() => {
    const totalTiles = rows * cols;
    const numericSeed = typeof seed === 'string' ? hashString(seed) : seed;
    const rng = mulberry32(numericSeed);
    
    // Generate tiles with randomized properties using token-based ranges
    const jitterMax = 24; // Will be overridden by CSS var if available
    const depthRange = 50; // Will be overridden by CSS var if available
    
    const tiles = Array.from({ length: totalTiles }, (_, index) => ({
      index,
      // Randomized jitter in range [-max, +max]
      jx: (rng() * 2 - 1) * jitterMax,
      jy: (rng() * 2 - 1) * jitterMax,
      // Randomized depth in range [min, max]
      z0: (rng() * 2 - 1) * depthRange,
      // Randomized phase for depth cycling
      phase: rng() * Math.PI * 2,
      // Randomized frequency multiplier
      freq: rng() * 0.8 + 0.2, // 0.2 to 1.0
      // Randomized materialization order
      order: rng(),
      // Delay for staggered animation
      delay: 0
    }));
    
    // Sort by order to get scrambled sequence
    tiles.sort((a, b) => a.order - b.order);
    
    // Assign staggered delays using token-based timing
    const maxDelay = 800; // Will use token-based timing in implementation
    tiles.forEach((tile, i) => {
      tile.delay = (i / totalTiles) * maxDelay;
    });
    
    return tiles;
  }, [seed, rows, cols]);
}

const MosaicLoader: React.FC<MosaicLoaderProps> = ({
  rows: propRows,
  cols: propCols,
  density = 'normal',
  durationMs,
  seed = 'mosaic-default',
  loopDepth = true,
  progress,
  variant = 'default',
  size = 'normal',
  spacing = 'normal',
  className = ''
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  
  // Calculate grid dimensions
  const { rows, cols } = useMemo(() => {
    if (propRows && propCols) {
      return { rows: propRows, cols: propCols };
    }
    
    // Auto-calculate based on density and viewport
    const densityConfig = {
      sparse: { baseRows: 8, baseCols: 14 },
      normal: { baseRows: 12, baseCols: 20 },
      dense: { baseRows: 16, baseCols: 28 }
    };
    
    const config = densityConfig[density];
    
    // Scale for smaller screens
    if (typeof window !== 'undefined' && window.innerWidth < 768) {
      return {
        rows: Math.max(6, Math.floor(config.baseRows * 0.7)),
        cols: Math.max(10, Math.floor(config.baseCols * 0.7))
      };
    }
    
    return { rows: config.baseRows, cols: config.baseCols };
  }, [propRows, propCols, density]);
  
  // Get token-derived duration default
  const effectiveDurationMs = useMemo(() => {
    if (durationMs) return durationMs;
    
    // Try to read from CSS custom property, fallback to 1500ms
    if (typeof window !== 'undefined' && containerRef.current) {
      const styles = getComputedStyle(containerRef.current);
      const slowMs = parseInt(styles.getPropertyValue('--motion-duration-slow').replace('ms', '')) || 320;
      return slowMs * 6; // Approximate 1920ms for slow mosaic materialization
    }
    return 1920; // Token-derived fallback
  }, [durationMs]);
  
  // Generate deterministic tile sequence
  const tiles = useMosaicSequence(seed, rows, cols);
  
  // Calculate depth amplitude based on progress
  const depthAmplitude = useMemo(() => {
    if (progress !== undefined) {
      return Math.max(0, 15 * (1 - progress)); // Decay from 15px to 0
    }
    return 10; // Default amplitude
  }, [progress]);
  
  // Set up progress animation if not manually controlled
  useEffect(() => {
    if (progress === undefined && containerRef.current) {
      const container = containerRef.current;
      container.style.setProperty('--mosaic-progress', '0');
      
      const startTime = Date.now();
      const animate = () => {
        const elapsed = Date.now() - startTime;
        const currentProgress = Math.min(elapsed / effectiveDurationMs, 1);
        
        container.style.setProperty('--mosaic-progress', currentProgress.toString());
        
        if (currentProgress < 1) {
          requestAnimationFrame(animate);
        }
      };
      
      requestAnimationFrame(animate);
    }
  }, [effectiveDurationMs, progress]);
  
  // Build CSS classes
  const containerClasses = [
    'mosaic',
    size === 'small' && 'mosaic-small',
    size === 'large' && 'mosaic-large',
    spacing === 'dense' && 'mosaic-dense',
    spacing === 'spacious' && 'mosaic-spacious',
    variant === 'accent' && 'mosaic-accent',
    variant === 'success' && 'mosaic-success',
    variant === 'gradient' && 'mosaic-gradient',
    variant === 'agi' && 'mosaic-agi',
    variant === 'agi-accent' && 'mosaic-agi-accent',
    variant === 'agi-cyberpunk' && 'mosaic-agi-cyberpunk',
    variant === 'dark' && 'mosaic-dark',
    variant === 'dark-soft' && 'mosaic-dark-soft',
    className
  ].filter(Boolean).join(' ');
  
  return (
    <div
      ref={containerRef}
      className={containerClasses}
      style={{
        '--mosaic-cols': cols,
        '--mosaic-rows': rows,
        ...(progress !== undefined && {
          '--mosaic-progress': progress,
          '--depth-amp': `${depthAmplitude}px`
        })
      } as React.CSSProperties}
    >
      {tiles.map((tile) => (
        <div
          key={tile.index}
          className={`mosaic-tile ${loopDepth ? 'depth-cycle' : ''}`}
          style={{
            '--jx': `${tile.jx}px`,
            '--jy': `${tile.jy}px`,
            '--z0': `${tile.z0}px`,
            '--phase': tile.phase,
            '--freq': tile.freq,
            '--delay': `${tile.delay}ms`,
            '--depth-amp': `${depthAmplitude}px`
          } as React.CSSProperties}
        />
      ))}
    </div>
  );
};

export default MosaicLoader;